import { SignedIn, SignedOut, useClerk } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { useEffect, type PropsWithChildren } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { authEvents } from '@core/api/authEvents';
import { isApiError } from '@core/api/errors';
import { qk } from '@core/api/queryKeys';
import { useTranslation } from '@core/i18n';
import { organizationStore, useSelectedOrganizationId, useSwitchOrganization } from '@core/org';
import { Button, EmptyState, ErrorState } from '@core/ui';

import { AppSplash } from './AppSplash';
import { useCurrentUser } from './useCurrentUser';

/**
 * Blocks rendering until `/users/me` resolves, and explains itself if it cannot.
 *
 * The gate matters: without it a screen paints with `role: undefined`, hides every admin
 * control, then flickers them in a beat later -- and a `RequireRole` evaluated during
 * that window briefly tells an admin they lack permission.
 *
 * The error branch matters just as much. A signed-in user whose `/me` fails is in the
 * single most confusing state the app has: Clerk is happy, so there is no sign-in prompt,
 * but the application has no idea who they are. Leaving the splash up spins forever with
 * no explanation.
 *
 * <p>The failures split in two, and offering the wrong action for the wrong one is its own
 * bug. A refused account is a decision ABOUT this person: retrying will never change it
 * and signing out is the only move. Everything else -- a backend still starting, a JWKS
 * fetch that lost a race, a token that expired in flight after the interceptor had already
 * spent its one retry -- is a decision about the SERVER, and signing out is actively
 * harmful there: it discards a perfectly good session to fix something that was never
 * wrong with it. Seen in practice when a backend restart left this screen up with Sign out
 * as the only option, while a plain reload would have worked.
 *
 * <p>And a third kind, since one person can belong to several organizations: a decision
 * about the ORGANIZATION this tab selected. It was suspended, or the person was removed
 * from it. Neither is about the person or the server, and the fix is to drop the
 * selection and let the server pick another.
 */
/** Codes that are a decision about this person, where retrying cannot help. */
const TERMINAL_CODES = new Set(['NO_PENDING_INVITE', 'ACCOUNT_DISABLED']);

/** The only screens a platform admin with no organization of their own can use. */
const PLATFORM_HOME = '/platform/organizations';

function MeGate({ children }: PropsWithChildren) {
  const me = useCurrentUser();
  const { signOut } = useClerk();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const selected = useSelectedOrganizationId();
  const switchOrganization = useSwitchOrganization();

  const error = isApiError(me.error) ? me.error : null;
  // The selected organization no longer resolves for this person: removed from it, or it
  // was deleted. Nothing to tell them -- the server's default is the right place to land.
  const selectionGone = error?.status === 404 && selected !== null;
  const suspended = error?.code === 'ORGANIZATION_SUSPENDED';
  const serverOrganizationId = me.organization === undefined ? undefined : me.organization?.id;

  useEffect(() => {
    if (selectionGone) void switchOrganization(null);
  }, [selectionGone, switchOrganization]);

  // Pin this tab to whichever organization the server actually resolved. Without it a
  // tab that started on the default would follow the default when it changes elsewhere,
  // and a request that raced a switch could act in a different tenant from the screen.
  useEffect(() => {
    if (serverOrganizationId === undefined) return;
    organizationStore.set(serverOrganizationId ?? null);
  }, [serverOrganizationId]);

  // An organization suspended (or left) mid-session surfaces on whatever request hits it
  // first. Re-asking `/me` turns that into the right full-screen state below -- unless
  // `/me` is itself the request in flight, which would make this a refetch loop.
  useEffect(
    () =>
      authEvents.on('organization-unavailable', () => {
        if (queryClient.isFetching({ queryKey: qk.me() }) > 0) return;
        void queryClient.invalidateQueries({ queryKey: qk.me() });
      }),
    [queryClient],
  );

  if (me.isLoading || selectionGone) return <AppSplash label={t.account.signingIn} />;

  const signOutButton = (
    <Button variant="ghost" className="mt-2" onClick={() => void signOut()}>
      {t.common.signOut}
    </Button>
  );

  if (suspended) {
    return (
      <div className="bg-bg grid min-h-dvh place-items-center p-6">
        <div className="flex flex-col items-center">
          <ErrorState
            title={t.account.suspended}
            message={t.account.suspendedBody}
            traceId={error?.traceId}
          />
          {selected !== null && (
            <Button variant="secondary" onClick={() => void switchOrganization(null)}>
              {t.account.continueElsewhere}
            </Button>
          )}
          {signOutButton}
        </div>
      </div>
    );
  }

  if (me.error) {
    const terminal = error?.code != null && TERMINAL_CODES.has(error.code);

    return (
      <div className="bg-bg grid min-h-dvh place-items-center p-6">
        <ErrorState
          title={terminal ? t.account.notInvited : t.account.loadFailed}
          message={
            terminal
              ? (error?.message ?? t.account.notInvitedBody)
              : // Deliberately not the server's own wording. "Authentication is required"
                // is true and useless to somebody who is already signed in; what they need
                // to know is that it is probably not about them and is worth one retry.
                t.account.loadFailedBody
          }
          traceId={error?.traceId}
          onRetry={terminal ? undefined : me.retry}
        />
        {signOutButton}
      </div>
    );
  }

  if (me.organization === null) {
    // A platform administrator outside every tenant still has the whole platform to run.
    if (me.platformAdmin) {
      if (!pathname.startsWith('/platform')) return <Navigate to={PLATFORM_HOME} replace />;
      return <>{children}</>;
    }

    // Signed in through Clerk, but nobody has invited them anywhere. Not an error: it is
    // the expected first screen for someone who found the sign-up page on their own.
    return (
      <div className="bg-bg grid min-h-dvh place-items-center p-6">
        <div className="flex flex-col items-center">
          <EmptyState
            icon={<Building2 />}
            title={t.account.noOrganization}
            description={t.account.noOrganizationBody}
          />
          {signOutButton}
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/** Route element for the authenticated shell. */
export function RequireAuth() {
  const location = useLocation();

  return (
    <>
      <SignedIn>
        <MeGate>
          <Outlet />
        </MeGate>
      </SignedIn>
      <SignedOut>
        <Navigate
          to="/sign-in"
          replace
          // So the user lands where they were going, not on the dashboard.
          state={{ from: location.pathname + location.search }}
        />
      </SignedOut>
    </>
  );
}
